/**
 * 主題詳細頁的「對象與誘因」tab — 8 步價值鏈 Step 2
 *
 * 對象掛在主題下；誘因屬於對象。議題層不掛對象。
 * AI 建議分兩個入口：
 *   - ✨ AI 建議對象（從零生成 N 個對象 + 初步誘因）
 *   - ✨ AI 建議誘因（給已存在的對象，補誘因清單）
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Stack, Button, Chip, IconButton, Tooltip, Typography,
  Paper, CircularProgress, Checkbox,
  Card, CardContent, CardActions,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Select, FormControl, InputLabel, Slider,
  Divider, Alert,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  AutoAwesome as MagicIcon, Refresh as RefreshIcon,
  Person as PersonIcon, Star as StarIcon,
} from '@mui/icons-material';

import { useMessage } from '../../contexts/MessageContext';
import { usePermission } from '../../hooks/usePermission';
import { logCreate, logUpdate, logDelete } from '../../utils/userLogHelper';
import {
  Audience, AudienceCreate, AudienceUpdate, AudienceType,
  IncentivePoint, AudienceSuggestion, IncentiveSuggestion,
} from '../../types/research';
import {
  listAudiences, createAudience, updateAudience, deleteAudience,
  suggestAudiences, suggestIncentives,
} from '../../services/researchService';

const FUNC_CODE = 'ee_research_subjects';

const AUDIENCE_TYPES: { value: AudienceType; label: string; emoji: string }[] = [
  { value: 'government', label: '政府機關', emoji: '🏛️' },
  { value: 'investor',   label: '投資人',   emoji: '💰' },
  { value: 'technical',  label: '技術主管', emoji: '🔧' },
  { value: 'executive',  label: '高階主管', emoji: '👔' },
  { value: 'public',     label: '公眾',     emoji: '🌐' },
  { value: 'other',      label: '其他',     emoji: '👥' },
];

const typeMeta = (t?: AudienceType | null) =>
  AUDIENCE_TYPES.find(x => x.value === t) ?? AUDIENCE_TYPES[5];

interface Props {
  subjectId: number;
  subjectName: string;
  canManage: boolean;
}

const emptyForm: AudienceCreate = {
  audience_name: '',
  audience_type: 'other',
  persona: '',
  incentives: [],
  priority: 100,
  is_active: true,
};

const SubjectAudiencesTab: React.FC<Props> = ({ subjectId, subjectName, canManage }) => {
  const { showSuccess, showApiError } = useMessage();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(FUNC_CODE, 'create');
  const canDelete = hasPermission(FUNC_CODE, 'delete');

  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [loading, setLoading] = useState(false);

  // 編輯 dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<Audience | null>(null);
  const [form, setForm] = useState<AudienceCreate>(emptyForm);
  const [saving, setSaving] = useState(false);

  // AI 建議對象 dialog
  const [aiAudOpen, setAiAudOpen] = useState(false);
  const [aiAudLoading, setAiAudLoading] = useState(false);
  const [aiAudSuggestions, setAiAudSuggestions] = useState<AudienceSuggestion[]>([]);
  const [aiAudPicked, setAiAudPicked] = useState<Set<number>>(new Set());

  // AI 建議誘因（在編輯 dialog 內觸發）
  const [aiIncLoading, setAiIncLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listAudiences(subjectId);
      setAudiences(data);
    } catch (err: any) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  }, [subjectId, showApiError]);

  useEffect(() => { reload(); }, [reload]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setEditOpen(true);
  };

  const openEdit = (a: Audience) => {
    setEditing(a);
    setForm({
      audience_name: a.audience_name,
      audience_type: a.audience_type ?? 'other',
      persona: a.persona ?? '',
      incentives: [...(a.incentives ?? [])],
      priority: a.priority,
      is_active: a.is_active,
    });
    setEditOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: AudienceCreate = {
        ...form,
        audience_name: form.audience_name.trim(),
        persona: form.persona?.trim() || undefined,
      };
      if (editing) {
        await updateAudience(editing.id, payload as AudienceUpdate);
        logUpdate(FUNC_CODE, { ...editing }, { ...payload, id: editing.id });
        showSuccess('SYS020002', { name: payload.audience_name, id: String(editing.id) });
      } else {
        const created = await createAudience(subjectId, payload);
        logCreate(FUNC_CODE, { ...created });
        showSuccess('SYS020001', { name: payload.audience_name });
      }
      setEditOpen(false);
      reload();
    } catch (err: any) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (a: Audience) => {
    if (!window.confirm(`確定刪除對象「${a.audience_name}」？\n${a.incentives.length} 個誘因點會一併刪除。`)) return;
    try {
      await deleteAudience(a.id);
      logDelete(FUNC_CODE, { ...a });
      showSuccess('SYS020003', { name: a.audience_name, id: String(a.id) });
      reload();
    } catch (err: any) {
      showApiError(err);
    }
  };

  // ──── AI 建議對象 ────
  const openAiAud = async () => {
    setAiAudOpen(true);
    setAiAudSuggestions([]);
    setAiAudPicked(new Set());
    setAiAudLoading(true);
    try {
      const sugs = await suggestAudiences(subjectId, 5);
      setAiAudSuggestions(sugs);
      // 預設全勾
      setAiAudPicked(new Set(sugs.map((_, i) => i)));
    } catch (err: any) {
      showApiError(err);
    } finally {
      setAiAudLoading(false);
    }
  };

  const handleAdoptAiAudiences = async () => {
    if (aiAudPicked.size === 0) {
      setAiAudOpen(false);
      return;
    }
    let ok = 0, fail = 0;
    for (let i = 0; i < aiAudSuggestions.length; i++) {
      if (!aiAudPicked.has(i)) continue;
      const s = aiAudSuggestions[i];
      try {
        await createAudience(subjectId, {
          audience_name: s.audience_name,
          audience_type: (s.audience_type as AudienceType) ?? 'other',
          persona: s.persona ?? undefined,
          incentives: (s.suggested_incentives ?? []).map(p => ({
            point: p.point, weight: p.weight, evidence: p.evidence_hint ?? undefined,
          })),
        });
        ok++;
      } catch {
        fail++;
      }
    }
    showSuccess('SYS020001', {
      name: `AI 建議對象採納完成（成功 ${ok}${fail ? ` / 失敗 ${fail}` : ''}）`,
    });
    setAiAudOpen(false);
    reload();
  };

  // ──── AI 建議誘因（在 edit dialog 內）────
  const handleSuggestIncentives = async () => {
    if (!form.audience_name.trim()) return;
    setAiIncLoading(true);
    try {
      const sugs: IncentiveSuggestion[] = await suggestIncentives(
        subjectId, form.audience_name.trim(), form.persona || undefined, 6,
      );
      // 直接 append 到既有誘因清單末尾（不去重，user 自己看）
      const newPoints: IncentivePoint[] = sugs.map(s => ({
        point: s.point,
        weight: s.weight,
        evidence: s.evidence_hint ?? undefined,
      }));
      setForm(f => ({ ...f, incentives: [...(f.incentives ?? []), ...newPoints] }));
      showSuccess('SYS020001', { name: `AI 補了 ${newPoints.length} 個誘因` });
    } catch (err: any) {
      showApiError(err);
    } finally {
      setAiIncLoading(false);
    }
  };

  // ──── 誘因清單 inline 編輯 ────
  const updateIncentive = (idx: number, patch: Partial<IncentivePoint>) => {
    setForm(f => ({
      ...f,
      incentives: (f.incentives ?? []).map((p, i) => i === idx ? { ...p, ...patch } : p),
    }));
  };
  const addIncentive = () => {
    setForm(f => ({ ...f,
      incentives: [...(f.incentives ?? []), { point: '', weight: 0.5, evidence: '' }],
    }));
  };
  const removeIncentive = (idx: number) => {
    setForm(f => ({ ...f,
      incentives: (f.incentives ?? []).filter((_, i) => i !== idx),
    }));
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }} spacing={1}>
        <Typography variant="body2" color="text.secondary">
          主題「{subjectName}」面對的對象與誘因（Step 2）
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {canManage && (
          <Button
            size="small" variant="outlined"
            startIcon={<MagicIcon />}
            onClick={openAiAud}
          >✨ AI 建議對象</Button>
        )}
        {canCreate && (
          <Button
            size="small" variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
          >新增對象</Button>
        )}
        <Button
          size="small" variant="outlined"
          startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={reload} disabled={loading}
        >重新整理</Button>
      </Stack>

      {audiences.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          {loading ? '載入中…' : '尚未建立對象 — 直接新增 或 點 ✨ AI 建議對象 讓 AI 拆出'}
        </Paper>
      ) : (
        <Stack spacing={2}>
          {audiences.map(a => {
            const meta = typeMeta(a.audience_type);
            return (
              <Card key={a.id} variant="outlined">
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>{meta.emoji}</span>
                      {a.audience_name}
                    </Typography>
                    <Chip size="small" label={meta.label} variant="outlined" />
                    {a.created_by_ai && (
                      <Tooltip title={`AI 拆出 (model: ${a.ai_model_used || 'unknown'}, conf: ${a.ai_confidence?.toFixed(2) || '?'})`}>
                        <Chip size="small" icon={<MagicIcon />} label="AI" color="secondary" />
                      </Tooltip>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      優先序 {a.priority}
                    </Typography>
                  </Stack>
                  {a.persona && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      <PersonIcon fontSize="small" sx={{ verticalAlign: 'text-bottom', mr: 0.5 }} />
                      {a.persona}
                    </Typography>
                  )}
                  <Divider sx={{ mb: 1 }} />
                  <Typography variant="caption" color="text.secondary"
                              sx={{ display: 'block', mb: 0.5 }}>
                    誘因點（{a.incentives.length}）
                  </Typography>
                  {a.incentives.length === 0 ? (
                    <Typography variant="caption" color="text.disabled">
                      尚無誘因 — 編輯時可手動加 或 點 ✨ AI 建議誘因
                    </Typography>
                  ) : (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {a.incentives.map((p, i) => (
                        <Tooltip key={i} title={
                          <>
                            <div>權重 {p.weight.toFixed(2)}</div>
                            {p.evidence && <div>佐證：{p.evidence}</div>}
                          </>
                        }>
                          <Chip
                            size="small"
                            icon={<StarIcon sx={{
                              fontSize: 14,
                              opacity: Math.max(0.3, p.weight),
                            }} />}
                            label={p.point}
                            sx={{
                              borderColor: 'primary.main',
                              opacity: Math.max(0.5, 0.5 + p.weight * 0.5),
                            }}
                            variant="outlined"
                          />
                        </Tooltip>
                      ))}
                    </Stack>
                  )}
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                  {canManage && (
                    <Tooltip title="編輯對象 / 誘因">
                      <IconButton size="small" onClick={() => openEdit(a)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="刪除">
                      <IconButton size="small" color="error" onClick={() => handleDelete(a)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </CardActions>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* 編輯 dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? '編輯對象' : '新增對象'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField
                label="對象名稱" required size="small" fullWidth
                value={form.audience_name}
                onChange={e => setForm({ ...form, audience_name: e.target.value })}
                placeholder="例：政府機關主管"
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>類型</InputLabel>
                <Select
                  label="類型"
                  value={form.audience_type ?? 'other'}
                  onChange={e => setForm({ ...form, audience_type: e.target.value as AudienceType })}
                >
                  {AUDIENCE_TYPES.map(t => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.emoji} {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <TextField
              label="對象畫像（背景 / 痛點 / 決策邏輯）"
              size="small" multiline rows={3} fullWidth
              value={form.persona ?? ''}
              onChange={e => setForm({ ...form, persona: e.target.value })}
            />
            <Divider>
              <Typography variant="caption" color="text.secondary">誘因點</Typography>
            </Divider>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" startIcon={<AddIcon />}
                      onClick={addIncentive}>加誘因</Button>
              <Tooltip title={!form.audience_name.trim()
                ? '請先填對象名稱'
                : 'AI 推薦該對象的誘因點清單'}>
                <span>
                  <Button size="small" variant="outlined"
                          startIcon={aiIncLoading ? <CircularProgress size={14} /> : <MagicIcon />}
                          disabled={!form.audience_name.trim() || aiIncLoading}
                          onClick={handleSuggestIncentives}>
                    ✨ AI 建議誘因
                  </Button>
                </span>
              </Tooltip>
            </Stack>
            {(form.incentives ?? []).length === 0 && (
              <Alert severity="info">尚無誘因點 — 加幾個吧</Alert>
            )}
            {(form.incentives ?? []).map((p, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" sx={{ minWidth: 24 }}>#{idx + 1}</Typography>
                    <TextField
                      size="small" fullWidth placeholder="一句話的吸引力..."
                      value={p.point}
                      onChange={e => updateIncentive(idx, { point: e.target.value })}
                    />
                    <IconButton size="small" color="error"
                                onClick={() => removeIncentive(idx)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ pl: 4, mb: 1 }}>
                    <Typography variant="caption" sx={{ minWidth: 80, fontWeight: 500 }}>
                      權重 {p.weight.toFixed(2)}
                    </Typography>
                    <Slider
                      size="small"
                      value={p.weight} min={0} max={1} step={0.05}
                      onChange={(_, v) => updateIncentive(idx, { weight: v as number })}
                      marks={[
                        { value: 0 }, { value: 0.5 }, { value: 1 },
                      ]}
                      valueLabelDisplay="auto"
                      sx={{ flex: 1, mx: 2 }}
                    />
                  </Stack>
                  <TextField
                    size="small" fullWidth
                    placeholder="佐證方向（法規 / 案例 / 數據 / 趨勢...）"
                    value={p.evidence ?? ''}
                    onChange={e => updateIncentive(idx, { evidence: e.target.value })}
                    sx={{ pl: 4 }}
                  />
                </Stack>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>取消</Button>
          <Button onClick={handleSave} variant="contained"
                  disabled={saving || !form.audience_name.trim()}>
            {saving ? <CircularProgress size={18} /> : '儲存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI 建議對象 dialog */}
      <Dialog open={aiAudOpen} onClose={() => setAiAudOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          ✨ AI 建議對象 — {subjectName}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            勾選要採納的，每個對象連同建議誘因會一起建立
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {aiAudLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={32} />
              <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                AI 分析中…
              </Typography>
            </Box>
          ) : aiAudSuggestions.length === 0 ? (
            <Alert severity="warning">AI 沒拆出對象 — 主題描述太短或服務暫時不可用</Alert>
          ) : (
            <Stack spacing={1.5}>
              {aiAudSuggestions.map((s, i) => {
                const meta = typeMeta(s.audience_type);
                const checked = aiAudPicked.has(i);
                return (
                  <Paper key={i} variant="outlined"
                         sx={{ p: 1.5, bgcolor: checked ? 'action.selected' : 'inherit' }}>
                    <Stack direction="row" alignItems="flex-start" spacing={1}>
                      <Checkbox checked={checked}
                                onChange={() => {
                                  const next = new Set(aiAudPicked);
                                  if (next.has(i)) next.delete(i); else next.add(i);
                                  setAiAudPicked(next);
                                }} />
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="subtitle2">
                            {meta.emoji} {s.audience_name}
                          </Typography>
                          <Chip size="small" label={meta.label} variant="outlined" />
                        </Stack>
                        {s.persona && (
                          <Typography variant="caption" color="text.secondary"
                                      sx={{ display: 'block', mt: 0.5 }}>
                            {s.persona}
                          </Typography>
                        )}
                        {s.suggested_incentives.length > 0 && (
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              建議誘因（{s.suggested_incentives.length}）：
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap"
                                   useFlexGap sx={{ mt: 0.5 }}>
                              {s.suggested_incentives.map((p, j) => (
                                <Tooltip key={j} title={p.evidence_hint || ''}>
                                  <Chip size="small" label={p.point} variant="outlined"
                                        sx={{ opacity: 0.5 + p.weight * 0.5 }} />
                                </Tooltip>
                              ))}
                            </Stack>
                          </Box>
                        )}
                      </Box>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiAudOpen(false)}>取消</Button>
          <Button onClick={handleAdoptAiAudiences} variant="contained"
                  disabled={aiAudLoading || aiAudPicked.size === 0}>
            採納 {aiAudPicked.size} 個對象
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubjectAudiencesTab;
